export interface NormalizedRoomOption {
  id: string;
  name: string;
  description: string;
  imageUrl: string[];
}

function tryParseJsonArray(value: string): unknown[] | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith('[')) return null;

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeRoomOptionItem(room: unknown): NormalizedRoomOption | null {
  if (typeof room === 'string') {
    const parsed = tryParseJsonArray(room);
    if (parsed) return null;

    const name = room.trim();
    return name ? { id: name, name, description: '', imageUrl: [] } : null;
  }

  if (!room || typeof room !== 'object' || Array.isArray(room)) {
    return null;
  }

  const roomObj = room as {
    id?: string;
    _id?: string;
    uid?: string;
    name?: string;
    title?: string;
    description?: string;
    imageUrl?: string[];
  };

  const rawName = String(roomObj.name || roomObj.title || '').trim();
  if (rawName && tryParseJsonArray(rawName)) return null;

  const name = rawName;
  const id = String(roomObj.id || roomObj._id || roomObj.uid || name || '').trim();

  if (!id && !name) return null;

  return {
    id: id || name,
    name: name || id,
    description: roomObj.description || '',
    imageUrl: Array.isArray(roomObj.imageUrl) ? roomObj.imageUrl : [],
  };
}

/**
 * Normalizes room option data from API responses.
 * Handles string[], object[], JSON strings, and mixed legacy formats.
 */
export function parseRoomOptionsInput(value: unknown): NormalizedRoomOption[] {
  if (value == null) return [];

  if (typeof value === 'string') {
    const parsed = tryParseJsonArray(value);
    if (parsed) return parseRoomOptionsInput(parsed);

    const single = normalizeRoomOptionItem(value);
    return single ? [single] : [];
  }

  if (Array.isArray(value)) {
    const results: NormalizedRoomOption[] = [];

    for (const item of value) {
      if (typeof item === 'string') {
        const parsed = tryParseJsonArray(item);
        if (parsed) {
          results.push(...parseRoomOptionsInput(parsed));
          continue;
        }
      }

      const parsedItem = parseRoomOptionsInput(item);
      if (parsedItem.length > 0) {
        results.push(...parsedItem);
        continue;
      }

      const normalized = normalizeRoomOptionItem(item);
      if (normalized) results.push(normalized);
    }

    return dedupeRoomOptions(results);
  }

  if (typeof value === 'object') {
    const obj = value as { name?: string; title?: string };
    const rawName = String(obj.name || obj.title || '').trim();
    const parsedFromName = rawName ? tryParseJsonArray(rawName) : null;
    if (parsedFromName) return parseRoomOptionsInput(parsedFromName);

    const single = normalizeRoomOptionItem(value);
    return single ? [single] : [];
  }

  return [];
}

function dedupeRoomOptions(
  rooms: NormalizedRoomOption[]
): NormalizedRoomOption[] {
  const seen = new Set<string>();
  return rooms.filter(room => {
    const key = room.id || room.name;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function formatRoomOptionsLabel(rooms: NormalizedRoomOption[]): string {
  return rooms
    .map(room => room.name)
    .filter(Boolean)
    .join(', ');
}
